/**
 * 시공사례 등록·편집 폼.
 *
 * 업로드가 아니라 **URL 입력**이다 — Storage(R2·Supabase) 가 붙기 전까지는
 * 오늘의집·블로그에 이미 올라간 사진 주소를 그대로 붙여 넣어 쓴다. 붙이는 순간
 * 미리보기가 뜨므로 주소가 틀렸는지 바로 안다.
 *
 * 첫 장이 커버다. 커버를 따로 고르는 칸을 두면 「목록에 안 나오는 사례」 가 생긴다.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowDown, ArrowUp, Check, ImageOff, Plus, Star, X } from "lucide-react";
import { useRouter } from "@/lib/nav";
import { ReactNode, useEffect, useState } from "react";
import {
  Control,
  Controller,
  FieldError,
  Merge,
  useFieldArray,
  useForm,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import EmptyState from "@buildpy/ui/empty-state";
import SaveBar, { UNSAVED_LEAVE_CONFIRM } from "@/components/save-bar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@buildpy/ui/alert-dialog";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@buildpy/ui/select";
import { Switch } from "@buildpy/ui/switch";
import { Textarea } from "@buildpy/ui/textarea";
import { useLeaveGuard } from "@/hooks/use-leave-guard";
import { formatAmount } from "@buildpy/core/lib/format";
import { subjectJosa } from "@buildpy/core/lib/josa";
import { settleRedirect } from "@/lib/redirect-error";
import { useSignedUrls } from "@/lib/signed-url";
import {
  AREA_BAND_LABEL,
  AREA_BANDS,
  RESIDENCE_LABEL,
  RESIDENCES,
} from "@buildpy/core/modules/customer/types";
import {
  createPortfolio,
  deletePortfolio,
  updatePortfolio,
} from "@/api/actions/portfolio";
import { amountBandOf } from "@buildpy/core/modules/portfolio/amount-band";
import { excerptLineIssues, excerptLinesOf, timelineExcerptLines } from "@buildpy/core/modules/portfolio/normalize";
import { DELETED_SPACE_LABEL, PAIR_STAGE_LABEL } from "@buildpy/core/modules/portfolio/pairs";
import { isProjectPhotoPath } from "@buildpy/core/modules/portfolio/photo-path";
import { photoGroupsByDate, unpickedPhotos } from "@buildpy/core/modules/portfolio/photo-pick";
import { portfolioSavedMessage } from "@buildpy/core/modules/portfolio/saved-message";
import { PORTFOLIO_MAX_IMAGES, PORTFOLIO_URL_MESSAGE } from "@buildpy/core/modules/portfolio/schema";
import {
  Portfolio,
  PORTFOLIO_EXCERPTS_MAX,
  PORTFOLIO_IMAGE_STAGES,
  PORTFOLIO_PUBLIC_FIELD_LABEL,
  PORTFOLIO_PUBLIC_FIELDS,
  PORTFOLIO_PUBLIC_FIELDS_DEFAULT,
  PortfolioImageStage,
  PortfolioTimelineEntry,
} from "@buildpy/core/modules/portfolio/types";
import PortfolioLightbox from "@/modules/portfolio/ui/portfolio-lightbox";
import PortfolioPairPreview from "@/modules/portfolio/ui/portfolio-pair-preview";

/** Radix Select 는 빈 문자열 값을 허용하지 않는다. */
const NONE = "__none__";

const NO_SPACE_LABEL = "공간 선택 안 함";

const NO_CHOICE_LABEL = "선택 안 함";

const isHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname.length > 0;
  } catch {
    return false;
  }
};

// 현장 사진 장(photo_id)은 세션 경로, 나머지는 https 주소.
const isShowable = (url: string): boolean => isHttpsUrl(url) || isProjectPhotoPath(url);

const imageRowSchema = z
  .object({
    url: z.string().trim().max(1000, "주소가 너무 깁니다"),
    caption: z.string().trim().max(200, "200자 이내로 적어 주세요"),
    photo_id: z.string().optional(),
    // 사진 태그 — 전/후 칩과 공간 Select 가 고친다.
    stage: z.enum(PORTFOLIO_IMAGE_STAGES).optional(),
    space: z.string().optional(),
  })
  .superRefine((row, ctx) => {
    const ok = row.photo_id ? isProjectPhotoPath(row.url) : isHttpsUrl(row.url);
    if (!ok) ctx.addIssue({ code: z.ZodIssueCode.custom, message: PORTFOLIO_URL_MESSAGE, path: ["url"] });
  });

const STAGE_CHOICES: { value: PortfolioImageStage | undefined; label: string }[] = [
  { value: undefined, label: "없음" },
  { value: "BEFORE", label: PAIR_STAGE_LABEL.BEFORE },
  { value: "AFTER", label: PAIR_STAGE_LABEL.AFTER },
];

// 챕터 발췌 칸 — 한 줄에 발췌 하나, 서버와 같은 함수로 공백을 접고 빈 줄을 버린다.
const excerptTextLines = (text: string): string[] => excerptLinesOf(text.split("\n"));

const excerptTextSchema = z.string().superRefine((text, ctx) => {
  for (const message of excerptLineIssues(excerptTextLines(text))) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
});

const formSchema = z.object({
  title: z.string().trim().min(1, "제목을 입력하세요").max(100, "100자 이내로 적어 주세요"),
  summary: z.string().trim().max(500, "500자 이내로 적어 주세요"),
  body: z.string().trim().max(2000, "2000자 이내로 적어 주세요"),
  area_band: z.string(),
  residence: z.string(),
  tags: z.string().trim().max(300, "300자 이내로 적어 주세요"),
  published: z.boolean(),
  designer_id: z.string(),
  amount_won: z.string().trim().regex(/^[\d,\s]*$/, "숫자만 입력하세요").max(20, "금액이 너무 깁니다"),
  public_fields: z.array(z.enum(PORTFOLIO_PUBLIC_FIELDS)),
  images: z
    .array(imageRowSchema)
    .max(PORTFOLIO_MAX_IMAGES, `이미지는 ${PORTFOLIO_MAX_IMAGES}장까지 넣을 수 있습니다`),
  timeline_excerpts: z.array(excerptTextSchema),
});

type FormValues = z.infer<typeof formSchema>;

export interface DesignerOption {
  id: string;
  name: string;
}

export interface SpaceOption {
  value: string;
  label: string;
  // 이 사례가 쓰는 비활성·지운 코드 — 그 줄의 값만 지키고 새로 고르지는 못한다.
  disabled?: boolean;
  // 설정에서 지운 코드 — 공간별 묶음에서 목록 밖(코드마다 따로)으로 친다.
  unlisted?: boolean;
}

export interface ProjectPhotoOption {
  id: string;
  url: string;
  caption: string;
  // 기록 날짜(YYYY-MM-DD) — 고르기 후보를 날짜별로 묶는다.
  log_date?: string;
}

interface Props {
  /** 없으면 등록, 있으면 그 사례 수정. */
  portfolio?: Portfolio | null;
  // 현장에서 만든 사례면 그 현장 사진 — 「현장 사진에서 고르기」 후보.
  projectPhotos?: ProjectPhotoOption[];
  // 담당 디자이너 후보 — 이 조직 멤버.
  designers?: DesignerOption[];
  // 공간 태그 선택지 — 조직 `codes.space`(살아 있는 것 + 이 사례가 쓰는 것).
  spaceOptions?: SpaceOption[];
}

const digitsOf = (value: string): string => value.replace(/[,\s]/g, "");

export const toDefaults = (portfolio?: Portfolio | null): FormValues => ({
  title: portfolio?.title ?? "",
  summary: portfolio?.summary ?? "",
  body: portfolio?.body ?? "",
  area_band: portfolio?.area_band ?? "",
  residence: portfolio?.residence ?? "",
  tags: (portfolio?.tags ?? []).join(", "),
  published: portfolio?.published ?? false,
  designer_id: portfolio?.designer_id ?? "",
  amount_won: portfolio?.amount_won != null ? formatAmount(portfolio.amount_won) : "",
  public_fields: [...(portfolio?.public_fields ?? PORTFOLIO_PUBLIC_FIELDS_DEFAULT)],
  images: (portfolio?.images ?? []).map((image) => ({
    url: image.url,
    caption: image.caption,
    ...(image.photo_id ? { photo_id: image.photo_id } : {}),
    // 전/후·공간 Controller 가 마운트 때 빈 태그를 undefined 키로 심으므로 기준값에도 키를 둔다 — 없으면 무수정인데 isDirty 가 켜진다.
    stage: image.stage || undefined,
    space: image.space || undefined,
  })),
  timeline_excerpts: (portfolio?.timeline ?? []).map((chapter) => chapter.excerpts.join("\n")),
});

const PortfolioForm = ({ portfolio, projectPhotos = [], designers = [], spaceOptions = [] }: Props) => {
  const router = useRouter();
  const [failure, setFailure] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);
  const [onlyUnpicked, setOnlyUnpicked] = useState(false);
  // 처음 값을 고정해 입력칸 defaultValue 로도 싣는다 — register 는 값을 ref 로 나중에 넣어 서버 HTML 이 빈칸이 된다.
  const [defaults] = useState(() => toDefaults(portfolio));

  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults,
  });
  // 등록 화면은 바닥 저장 줄(SaveBar)이 떠나기 확인을 맡는다 — 두 번 묻지 않게 편집에서만 건다.
  const guarded = Boolean(portfolio) && isDirty;
  useLeaveGuard({ unload: guarded, links: guarded, back: guarded, message: UNSAVED_LEAVE_CONFIRM });
  // 저장한 값을 새 기준으로 삼는다 — RHF 권고대로 제출 콜백 밖(effect)에서 reset 한다.
  const [savedValues, setSavedValues] = useState<FormValues | null>(null);
  useEffect(() => {
    if (!savedValues) return;
    reset(savedValues);
    setSavedValues(null);
  }, [savedValues, reset]);

  const { append, fields, insert, move, remove } = useFieldArray({
    control,
    name: "images",
  });

  const images = useWatch({ control, name: "images" }) ?? [];
  // 현장 사진(/app/projects/…)은 img 가 Bearer 없이 못 열어 한 요청으로 서명받는다.
  const srcOf = useSignedUrls([...projectPhotos.map((photo) => photo.url), ...images.map((image) => image?.url ?? "")]);
  // dev 인메모리에 0046 이전 행이 남아 있을 수 있어 비면 빈 배열로 본다.
  const timeline = portfolio?.timeline ?? [];
  const amountText = useWatch({ control, name: "amount_won" }) ?? "";
  const amountDigits = digitsOf(amountText);
  const amountBand = /^\d+$/.test(amountDigits) ? amountBandOf(Number(amountDigits)) : null;
  const liveIds = new Set(projectPhotos.map((photo) => photo.id));
  // 현장에서 지워진 사진 장 — 자리만 보여 주고 뺄 수 있게 한다(저장하면 액션이 뺀다).
  const isGone = (image?: { photo_id?: string }): boolean => Boolean(image?.photo_id) && !liveIds.has(image?.photo_id ?? "");
  const preview = images.filter((image) => !isGone(image) && isShowable(image?.url ?? ""));
  // 공정 챕터 사진 수는 지금 장에 남은 현장 사진만 센다(뺀 사진은 공개 때도 안 나간다).
  const keptPhotoIds = new Set(images.flatMap((image) => (image?.photo_id && !isGone(image) ? [image.photo_id] : [])));
  const pickedIndex = (photoId: string) => images.findIndex((image) => image?.photo_id === photoId);
  const pickedPhotoIds = new Set(images.flatMap((image) => (image?.photo_id ? [image.photo_id] : [])));
  const photoGroups = photoGroupsByDate(onlyUnpicked ? unpickedPhotos(projectPhotos, pickedPhotoIds) : projectPhotos);
  // 현장 사진을 고를 수 없는 사례(직접 등록·사진 없는 현장)는 그 까닭을 이미지 설명에 적는다.
  const imageSourceHint =
    projectPhotos.length > 0
      ? "위에서 현장 사진을 고르거나 https:// 사진 주소를 붙여 넣습니다"
      : portfolio?.project_id
        ? "이 현장에 올린 사진이 아직 없어 https:// 사진 주소를 붙여 넣습니다"
        : "https:// 사진 주소를 붙여 넣습니다. 현장 사진은 현장 상세의 「시공사례 초안 만들기」 로 만든 사례에서 고를 수 있습니다";

  const togglePhoto = (photo: ProjectPhotoOption) => {
    const index = pickedIndex(photo.id);
    if (index >= 0) remove(index);
    else if (fields.length < PORTFOLIO_MAX_IMAGES) append({ url: photo.url, caption: photo.caption, photo_id: photo.id });
  };

  const makeCover = (photo: ProjectPhotoOption) => {
    const index = pickedIndex(photo.id);
    if (index > 0) move(index, 0);
    else if (index < 0 && fields.length < PORTFOLIO_MAX_IMAGES) insert(0, { url: photo.url, caption: photo.caption, photo_id: photo.id });
  };

  const submit = handleSubmit(async (values) => {
    setFailure(null);

    const form = new FormData();
    form.set("title", values.title);
    form.set("summary", values.summary);
    form.set("body", values.body);
    form.set("area_band", values.area_band);
    form.set("residence", values.residence);
    form.set("tags", values.tags);
    form.set("published", values.published ? "true" : "false");
    form.set("images", JSON.stringify(values.images));
    form.set("designer_id", values.designer_id);
    form.set("amount_won", digitsOf(values.amount_won));
    form.set("public_fields", JSON.stringify(values.public_fields));
    // 챕터가 있을 때만 보낸다 — 서버는 챕터별 발췌만 받고 이름·날짜·사진은 저장된 값을 쓴다.
    if (portfolio && timeline.length > 0) {
      form.set("timeline_excerpts", JSON.stringify(values.timeline_excerpts.map(excerptTextLines)));
    }
    // 첫 장이 커버다. 저장소도 비어 있으면 첫 장을 쓰지만 뜻을 여기서 못 박는다.
    form.set("cover_url", values.images[0]?.url ?? "");

    try {
      if (portfolio) {
        const result = await updatePortfolio(portfolio.id, form);
        if (!result.ok) {
          setFailure(result.message);
          return;
        }
        toast.success(portfolioSavedMessage(result.notice));
        const dropped = new Set(result.dropped_photo_ids ?? []);
        const droppedAt = values.images.flatMap((image, i) => (image.photo_id && dropped.has(image.photo_id) ? [i] : []));
        if (droppedAt.length > 0) remove(droppedAt);
        // 발췌 칸은 서버가 저장한 모양(공백 접기·빈 줄 제거)으로 맞춘다 — 저장소와 같은 함수다.
        if (timeline.length > 0) {
          values.timeline_excerpts.forEach((text, i) =>
            setValue(`timeline_excerpts.${i}`, timelineExcerptLines(excerptTextLines(text)).join("\n"))
          );
        }
        setSavedValues(getValues());
        router.refresh();
        return;
      }
      // 성공하면 액션이 상세로 redirect 한다.
      const result = await createPortfolio(form);
      if (result?.ok === false) setFailure(result.message);
    } catch (error) {
      if (settleRedirect(error)) return;
      setFailure("저장하지 못했습니다. 입력을 확인하고 다시 시도하세요.");
    }
  });

  const onRemove = async () => {
    if (!portfolio) return;
    setRemoving(true);
    setFailure(null);
    try {
      const result = await deletePortfolio(portfolio.id);
      if (result?.ok === false) setFailure(result.message);
    } catch (error) {
      if (settleRedirect(error)) return;
      setFailure("삭제하지 못했습니다. 다시 시도해 주세요.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <form className="flex flex-col gap-y-6" onSubmit={submit}>
      <Section title="사례 정보">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Input
              {...register("title")}
              defaultValue={defaults.title}
              errorMessage={errors.title?.message}
              id="title"
              label="제목 *"
              placeholder="예: ○○동 30평대 아파트 욕실 리모델링"
            />
          </div>

          <div className="md:col-span-2">
            <Input
              {...register("summary")}
              defaultValue={defaults.summary}
              errorMessage={errors.summary?.message}
              id="summary"
              label="요약"
              placeholder="예: 한 줄로 공사 요지 — 거주 중 시공 · 주방 가구 교체"
            />
          </div>

          <div className="md:col-span-2">
            <Field htmlFor="body" label="본문">
              <Textarea {...register("body")} className="text-sm" defaultValue={defaults.body} id="body" placeholder="공사 기간 · 공정 · 현장 기록" rows={8} />
              {errors.body?.message && <p className="text-sm text-destructive">{errors.body.message}</p>}
            </Field>
          </div>

          <SelectField
            control={control}
            label="평형대"
            name="area_band"
            options={AREA_BANDS.map((value) => ({
              value,
              label: AREA_BAND_LABEL[value],
            }))}
          />
          <SelectField
            control={control}
            label="거주형태"
            name="residence"
            options={RESIDENCES.map((value) => ({
              value,
              label: RESIDENCE_LABEL[value],
            }))}
          />

          <div className="md:col-span-2">
            <Input
              {...register("tags")}
              defaultValue={defaults.tags}
              errorMessage={errors.tags?.message}
              id="tags"
              label="태그"
              placeholder="전체, 욕실, 가구 (쉼표로 구분)"
            />
          </div>

          <Field label="공개">
            <Controller
              control={control}
              name="published"
              render={({ field }) => (
                <div className="flex min-h-10 items-center gap-x-2">
                  <Switch
                    aria-describedby="published-label"
                    aria-label="공개"
                    checked={field.value}
                    id="published"
                    onCheckedChange={field.onChange}
                  />
                  <span className="text-sm text-muted-foreground" id="published-label">
                    {field.value
                      ? "공개 페이지에 올립니다(설정에서 공개 페이지를 켜면 누구나 볼 수 있음)"
                      : "내부에서만 보입니다"}
                  </span>
                </div>
              )}
            />
          </Field>
        </div>
      </Section>

      <Section description="공개 페이지에 보일 항목만 켭니다. 금액은 켜도 「3천만 원대」 처럼 구간으로만 보입니다." title="공개 항목">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Controller
              control={control}
              name="public_fields"
              render={({ field }) => (
                <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid="portfolio-public-fields">
                  {PORTFOLIO_PUBLIC_FIELDS.map((key) => (
                    <div key={key} className="flex items-center gap-x-2">
                      <Checkbox
                        checked={field.value.includes(key)}
                        id={`public-field-${key}`}
                        onCheckedChange={(checked) =>
                          field.onChange(
                            PORTFOLIO_PUBLIC_FIELDS.filter((k) => (k === key ? checked === true : field.value.includes(k)))
                          )
                        }
                      />
                      <Label htmlFor={`public-field-${key}`} variant="option">
                        {PORTFOLIO_PUBLIC_FIELD_LABEL[key]}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            />
          </div>

          <Field htmlFor="designer_id" label="담당 디자이너">
            <Controller
              control={control}
              name="designer_id"
              render={({ field }) => (
                <Select value={field.value || NONE} onValueChange={(value) => field.onChange(value === NONE ? "" : value)}>
                  <SelectTrigger className="text-sm" data-testid="portfolio-designer" id="designer_id" size="sm">
                    {/* 고른 이름을 직접 넣어 서버 렌더·하이드레이션 전에도 칸이 비지 않게 한다. */}
                    <SelectValue placeholder="선택">
                      {field.value ? designers.find((d) => d.id === field.value)?.name : NO_CHOICE_LABEL}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{NO_CHOICE_LABEL}</SelectItem>
                    {designers.map((designer) => (
                      <SelectItem key={designer.id} value={designer.id}>
                        {designer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>

          <div className="flex flex-col gap-y-1">
            <Input
              {...register("amount_won")}
              defaultValue={defaults.amount_won}
              data-testid="portfolio-amount"
              errorMessage={errors.amount_won?.message}
              id="amount_won"
              inputMode="numeric"
              inputSize="sm"
              label="공사 금액(원)"
              placeholder="예: 30,000,000"
            />
            <p className="text-xs text-muted-foreground" data-testid="portfolio-amount-band">
              {amountBand ? `공개 시 「${amountBand}」` : "공개 시 구간으로만 보입니다"}
            </p>
          </div>
        </div>
      </Section>

      {timeline.length > 0 && (
        <TimelineEditor
          chapters={timeline}
          defaultTexts={defaults.timeline_excerpts}
          errors={errors.timeline_excerpts}
          keptPhotoIds={keptPhotoIds}
          register={register}
        />
      )}

      {projectPhotos.length > 0 && (
        <Section
          description={`눌러서 넣고 빼고, 별로 대표(커버)를 정합니다 · 고른 사진 ${pickedPhotoIds.size} / 현장 사진 ${projectPhotos.length} · 사진은 로그인한 직원에게만 보입니다.`}
          title="현장 사진에서 고르기">
          <label className="flex min-h-10 items-center gap-x-2 text-sm" htmlFor="portfolio-photo-only-unpicked">
            <Switch
              aria-describedby="portfolio-photo-only-unpicked-label"
              aria-label="안 고른 사진만 보기"
              checked={onlyUnpicked}
              data-testid="portfolio-photo-only-unpicked"
              id="portfolio-photo-only-unpicked"
              onCheckedChange={setOnlyUnpicked}
            />
            <span id="portfolio-photo-only-unpicked-label">안 고른 사진만 보기 · {unpickedPhotos(projectPhotos, pickedPhotoIds).length}장</span>
          </label>
          <div className="flex flex-col gap-y-3" data-testid="portfolio-project-photos">
            {photoGroups.length === 0 && <EmptyState>현장 사진을 전부 골랐습니다.</EmptyState>}
            {photoGroups.map((group) => (
              <div key={group.date} className="flex flex-col gap-y-1.5" data-testid="portfolio-photo-group">
                <h3 className="text-xs font-medium tabular-nums text-muted-foreground">
                  {group.date || "날짜 없음"} · {group.photos.length}장
                </h3>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                  {group.photos.map((photo) => {
                    const index = pickedIndex(photo.id);
                    return (
                      <div key={photo.id} className="flex flex-col gap-y-1">
                        <button
                          aria-pressed={index >= 0}
                          className={`relative overflow-hidden rounded-md border-2 ${index >= 0 ? "border-primary" : "border-transparent opacity-60"}`}
                          data-testid="portfolio-photo-toggle"
                          type="button"
                          onClick={() => togglePhoto(photo)}>
                          <img alt={photo.caption} className="aspect-square w-full object-cover" loading="lazy" src={srcOf(photo.url)} />
                          {index >= 0 && (
                            <span className="absolute top-1 right-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                              <Check className="size-3" />
                            </span>
                          )}
                        </button>
                        <Button
                          data-testid="portfolio-photo-cover"
                          disabled={index === 0}
                          size="sm"
                          type="button"
                          variant="outline"
                          onClick={() => makeCover(photo)}>
                          <Star className="size-3" />
                          {index === 0 ? "대표" : "대표로"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section
        description={`${imageSourceHint} · 최대 ${PORTFOLIO_MAX_IMAGES}장 · 첫 장이 커버입니다.`}
        title="이미지">
        <div className="flex flex-col gap-y-2">
          {fields.length === 0 && (
            <EmptyState>
              아직 이미지가 없습니다.
            </EmptyState>
          )}

          {fields.map((field, index) => (
            <div
              key={field.id}
              className="grid grid-cols-1 items-start gap-2 rounded-md border border-border p-2.5 md:grid-cols-[4.5rem_1fr_1fr_auto]">
              <div className="flex items-center gap-x-2">
                <span className="text-xs text-muted-foreground">
                  {index === 0 ? "커버" : index + 1}
                </span>
                <Thumb gone={isGone(images[index])} src={srcOf(images[index]?.url ?? "")} url={images[index]?.url ?? ""} />
              </div>

              {images[index]?.photo_id ? (
                <span className="flex min-h-9 items-center text-sm text-muted-foreground" data-testid="portfolio-image-project-photo">
                  <input type="hidden" {...register(`images.${index}.url`)} defaultValue={field.url} />
                  {isGone(images[index]) ? "현장 사진(지워짐)" : "현장 사진"}
                </span>
              ) : (
                <Input
                  {...register(`images.${index}.url`)}
                  defaultValue={field.url}
                  inputSize="sm"
                  errorMessage={errors.images?.[index]?.url?.message}
                  id={`image-url-${index}`}
                  placeholder="https://…"
                />
              )}
              <Input
                {...register(`images.${index}.caption`)}
                defaultValue={field.caption}
                inputSize="sm"
                errorMessage={errors.images?.[index]?.caption?.message}
                id={`image-caption-${index}`}
                placeholder="캡션 (거실·주방 등)"
              />

              <div className="flex items-center gap-x-1">
                <Button
                  aria-label="위로"
                  disabled={index === 0}
                  size="icon"
                  type="button"
                  variant="outline"
                  onClick={() => move(index, index - 1)}>
                  <ArrowUp className="size-4" />
                </Button>
                <Button
                  aria-label="아래로"
                  disabled={index === fields.length - 1}
                  size="icon"
                  type="button"
                  variant="outline"
                  onClick={() => move(index, index + 1)}>
                  <ArrowDown className="size-4" />
                </Button>
                <Button
                  aria-label="이미지 빼기"
                  size="icon"
                  type="button"
                  variant="destructive"
                  onClick={() => remove(index)}>
                  <X className="size-4" />
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2 md:col-span-3 md:col-start-2" data-testid="portfolio-image-tags">
                <Controller
                  control={control}
                  name={`images.${index}.stage`}
                  render={({ field: stage }) => (
                    <div aria-label="전/후" className="flex rounded-md bg-muted p-0.5" role="group">
                      {STAGE_CHOICES.map((choice) => (
                        <Button
                          key={choice.label}
                          aria-pressed={stage.value === choice.value}
                          data-testid="portfolio-image-stage"
                          size="sm"
                          type="button"
                          variant={stage.value === choice.value ? "secondary" : "ghost"}
                          onClick={() => stage.onChange(choice.value)}>
                          {choice.label}
                        </Button>
                      ))}
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name={`images.${index}.space`}
                  render={({ field: space }) => {
                    const picked = spaceOptions.find((o) => o.value === space.value);
                    return (
                      <Select
                        value={picked ? picked.value : NONE}
                        onValueChange={(value) => space.onChange(value === NONE ? undefined : value)}>
                        <SelectTrigger
                          aria-label="공간"
                          className="w-32 text-sm"
                          data-testid="portfolio-image-space"
                          id={`image-space-${index}`}
                          size="sm">
                          {/* 고른 이름을 직접 넣어 서버 렌더·하이드레이션 전에도 칸이 비지 않게 한다. */}
                          <SelectValue placeholder="공간">{picked?.label ?? NO_SPACE_LABEL}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>{NO_SPACE_LABEL}</SelectItem>
                          {spaceOptions.map((option) => (
                            <SelectItem key={option.value} disabled={option.disabled} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  }}
                />
              </div>
            </div>
          ))}

          {errors.images?.message && (
            <p className="text-sm text-destructive">{errors.images.message}</p>
          )}

          <div className="flex items-center gap-x-2">
            <Button
              disabled={fields.length >= PORTFOLIO_MAX_IMAGES}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => append({ url: "", caption: "" })}>
              <Plus className="size-4" />
              이미지 추가
            </Button>
            <Button
              disabled={preview.length === 0}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setLightbox(0)}>
              미리보기
            </Button>
            <span className="text-xs text-muted-foreground">
              {fields.length} / {PORTFOLIO_MAX_IMAGES}
            </span>
          </div>
        </div>
      </Section>

      <Section
        description="사진마다 공사 전·후와 공간을 고르면 공간별로 묶입니다. 공개 페이지에는 전·후가 둘 다 있는 공간만 나란히 보입니다."
        title="공간별 공사 전·후">
        <PortfolioPairPreview
          images={images.map((image) => ({
            url: image && !isGone(image) && isShowable(image.url ?? "") ? (srcOf(image.url) ?? "") : "",
            ...(image?.stage ? { stage: image.stage } : {}),
            ...(image?.space ? { space: image.space } : {}),
          }))}
          spaces={spaceOptions.filter((option) => !option.unlisted).map((option) => ({ code: option.value, label: option.label }))}
          unlistedLabel={DELETED_SPACE_LABEL}
        />
      </Section>

      <PortfolioLightbox
        images={preview.map((image) => ({
          url: srcOf(image.url) ?? "",
          caption: image.caption ?? "",
        }))}
        openIndex={lightbox}
        onClose={() => setLightbox(null)}
      />

      {failure && <p className="text-sm text-destructive">{failure}</p>}

      {/* 등록 화면 — 폰은 긴 폼이라 바닥 저장 줄(SaveBar, 떠나기 확인도 맡는다), md 이상은 폼 끝 버튼. */}
      {!portfolio ? (
        <>
          <SaveBar alwaysEnabled className="bg-card md:hidden" dirty={isDirty} label="사례 등록" pending={isSubmitting} />
          <div className="hidden items-center justify-end gap-x-2 md:flex" data-testid="portfolio-submit-row">
            <Button disabled={isSubmitting} size="sm" type="submit">
              사례 등록
            </Button>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-between gap-x-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={removing} size="sm" type="button" variant="destructive">
                삭제
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>이 시공사례를 삭제할까요?</AlertDialogTitle>
                <AlertDialogDescription>
                  「{portfolio.title}」{subjectJosa(portfolio.title)} 목록에서 사라집니다. 되돌릴 수 없습니다.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>취소</AlertDialogCancel>
                <AlertDialogAction onClick={onRemove}>삭제</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button disabled={isSubmitting} size="sm" type="submit">
            저장
          </Button>
        </div>
      )}
    </form>
  );
};

export default PortfolioForm;

/* ── 조각들 ────────────────────────────────────────────────── */

const photoCount = (chapter: PortfolioTimelineEntry, kept: ReadonlySet<string>): number =>
  chapter.photo_ids.filter((id) => kept.has(id)).length;

// 공정 챕터 — 이름·날짜·사진은 초안 그대로, 발췌 줄만 고치거나 지운다.
const TimelineEditor = ({
  chapters,
  keptPhotoIds,
  register,
  errors,
  defaultTexts,
}: {
  chapters: PortfolioTimelineEntry[];
  defaultTexts: string[];
  keptPhotoIds: ReadonlySet<string>;
  register: UseFormRegister<FormValues>;
  errors?: Merge<FieldError, (FieldError | undefined)[]>;
}) => (
  <Section
    description={`현장 공정 날짜·일지·사진에서 뽑았습니다. 발췌는 한 줄에 하나(챕터마다 ${PORTFOLIO_EXCERPTS_MAX}줄) — 개인정보가 남았으면 지우세요. 공개 항목의 「공정 챕터」 를 켜면 이 순서로 보입니다.`}
    title="공정 챕터">
    <ol className="flex flex-col gap-y-2" data-testid="portfolio-timeline">
      {chapters.map((chapter, index) => (
        <li key={`${chapter.name}-${index}`} className="flex flex-col gap-y-1.5 rounded-md border border-border p-2.5">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-semibold">
              {index + 1}. {chapter.name}
            </span>
            <span className="text-xs text-muted-foreground">
              {chapter.start && chapter.end ? `${chapter.start} ~ ${chapter.end}` : "날짜 없음"}
              {photoCount(chapter, keptPhotoIds) > 0 ? ` · 사진 ${photoCount(chapter, keptPhotoIds)}장` : ""}
            </span>
          </div>
          <Textarea
            {...register(`timeline_excerpts.${index}`)}
            aria-label={`${chapter.name} 발췌`}
            defaultValue={defaultTexts[index]}
            className="min-h-[60px] text-xs"
            data-testid="portfolio-timeline-excerpts"
            id={`timeline-excerpts-${index}`}
            placeholder="발췌 없음"
            rows={Math.max(2, Math.min(chapter.excerpts.length, PORTFOLIO_EXCERPTS_MAX))}
          />
          {errors?.[index]?.message && <p className="text-sm text-destructive">{errors[index]?.message}</p>}
        </li>
      ))}
    </ol>
  </Section>
);

const Thumb = ({ url, src, gone }: { url: string; src: string | undefined; gone: boolean }) =>
  gone ? (
    <div
      className="flex size-10 flex-col items-center justify-center rounded-md bg-muted text-3xs leading-tight text-muted-foreground break-keep"
      data-testid="portfolio-photo-gone">
      <ImageOff className="size-3" />
      지워진 사진
    </div>
  ) : isShowable(url) ? (
    <img
      alt=""
      className="size-10 rounded-md object-cover"
      loading="lazy"
      src={src}
    />
  ) : (
    <div className="flex size-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <ImageOff className="size-4" />
    </div>
  );

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-y-3">
    <div className="flex flex-col gap-y-0.5">
      <h2 className="text-sm font-semibold">{title}</h2>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
    {children}
  </section>
);

const Field = ({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) => (
  <div className="flex w-full flex-col items-start gap-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
  </div>
);

const SelectField = ({
  control,
  name,
  label,
  options,
}: {
  control: Control<FormValues>;
  name: "area_band" | "residence";
  label: string;
  options: { value: string; label: string }[];
}) => (
  <Field htmlFor={name} label={label}>
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Select
          value={field.value || NONE}
          onValueChange={(value) => field.onChange(value === NONE ? "" : value)}>
          <SelectTrigger className="text-sm" id={name} size="sm">
            {/* 고른 이름을 직접 넣어 서버 렌더·하이드레이션 전에도 칸이 비지 않게 한다. */}
            <SelectValue placeholder="선택">
              {field.value ? options.find((o) => o.value === field.value)?.label : NO_CHOICE_LABEL}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{NO_CHOICE_LABEL}</SelectItem>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  </Field>
);
